import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Link, MenuItem,
  Paper, Select, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { createChipGuidanceDraft, getChipGuidance, getChipGuidanceDraft,
  publishChipGuidanceDraft, restoreChipGuidanceRevision, updateChipGuidanceDraft } from '../../services/chipLearningRulesService';

const read = (response) => {
  if (!response?.status) {
    const error = new Error(response?.message || response?.error || 'Unable to update CHIP guidance.');
    error.code = response?.code;
    throw error;
  }
  return response.data;
};
const conflictId = (conflict) => conflict.conflict_id || conflict.id;
// The merge pipeline reports structured validation results; the administrator only ever
// needs one plain sentence — never a JSON dump — because retrying or editing is the fix.
const friendly = (errors) => {
  const first = (Array.isArray(errors) ? errors : []).find(Boolean);
  const text = typeof first === 'string' ? first : first?.message || first?.details || first?.explanation || first?.issue;
  if (!text) return 'The AI could not merge this rule safely.';
  // Internal pipeline wording (classification/JSON shape problems) is not actionable for an
  // administrator; a retry is. Real validation sentences pass through unchanged.
  return /classif|omitted|did not|lost a source|unexpected or duplicate|JSON object|conflict result|ungrounded/i.test(String(text))
    ? 'The AI returned an unusable merge result. This is usually temporary — try again.'
    : String(text);
};
const POLL_MS = 3000;
const POLL_LIMIT_MS = 5 * 60 * 1000;

const SourceLinks = ({ refs = [], onSource }) => <Stack direction="row" spacing={1} flexWrap="wrap">
  {refs.map((reference) => <Link component="button" type="button" key={reference} onClick={() => onSource?.(reference)} sx={{ fontSize: 12 }}>Source {reference}</Link>)}
</Stack>;

const GuidancePanel = forwardRef(function GuidancePanel({ customerId = 0, sourceHistory, onPublished, onSource, refreshToken = 0, focusRule = null }, ref) {
  const [guidance, setGuidance] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailsTab, setDetailsTab] = useState('guidance');
  const [highlightKey, setHighlightKey] = useState(null);
  const historyPanel = useRef(null);
  const handledFocus = useRef(null);
  // One silent merge job at a time: phase 'working' shows only a spinner, 'conflict' asks the
  // administrator to choose between contradicting rules, 'failed' offers a retry. Nothing else
  // of the merge/validate/publish pipeline is ever shown.
  const [job, setJob] = useState(null);
  const [resolutions, setResolutions] = useState({});
  const [busy, setBusy] = useState(false);
  const [restore, setRestore] = useState(null);
  const mounted = useRef(true);
  const sequence = useRef(0);
  const jobSequence = useRef(0);
  const current = useRef({});
  current.current = { customerId, onPublished };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; sequence.current += 1; jobSequence.current += 1; }; }, []);
  useEffect(() => { setRestore(null); setGuidance(null); }, [customerId]);

  const load = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    try {
      const published = await getChipGuidance(customerId);
      if (!mounted.current || request !== sequence.current) return;
      setGuidance(read(published)); setError('');
    } catch (failure) { if (mounted.current && request === sequence.current) { setGuidance(null); setError(failure.message); } }
    finally { if (mounted.current && request === sequence.current) setLoading(false); }
  }, [customerId]);
  useEffect(() => { load(); }, [load, refreshToken]);
  // A focused rule (the inbox's Accepted chip, a suggestion's "Open in guidance") scrolls to the
  // section citing it once this scope's guidance is loaded; a rule no section cites (switched off)
  // opens the source history instead. Each focus is handled once.
  useEffect(() => {
    if (!focusRule?.token || !guidance || Number(guidance.customer_id) !== Number(customerId) || handledFocus.current === focusRule.token) return undefined;
    handledFocus.current = focusRule.token;
    const section = (guidance.sections || []).find((entry) => (entry.source_refs || []).includes(`rule:${focusRule.id}`));
    if (!section) {
      setDetailsTab('history');
      historyPanel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      return undefined;
    }
    setHighlightKey(section.key);
    document.getElementById(`guidance-section-${section.key}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightKey(null), 2500);
    return () => clearTimeout(timer);
  }, [focusRule, guidance, customerId]);

  const run = useCallback(async (operation, selection) => {
    const token = ++jobSequence.current;
    const sleep = (milliseconds) => new Promise((resolve) => { setTimeout(resolve, milliseconds); });
    const waitForDraft = async (draft) => {
      const startedAt = Date.now();
      let polled = draft;
      while (polled && ['generating', 'validating'].includes(polled.status)) {
        if (Date.now() - startedAt >= POLL_LIMIT_MS) {
          throw new Error('The merge is taking longer than expected. Nothing was published; try again in a moment.');
        }
        await sleep(POLL_MS);
        if (token !== jobSequence.current || !mounted.current) return null;
        polled = read(await getChipGuidanceDraft(polled.id));
        if (token !== jobSequence.current || !mounted.current) return null;
      }
      return polled;
    };
    setResolutions({});
    setJob({ selection, phase: 'working' });
    try {
      const draft = read(await operation());
      if (token !== jobSequence.current || !mounted.current) return;
      const settled = await waitForDraft(draft);
      if (!settled || token !== jobSequence.current || !mounted.current) return;
      if ((settled.conflicts || []).length) { setJob({ selection, phase: 'conflict', draft: settled }); return; }
      if (settled.status !== 'ready' || (settled.errors || []).length) {
        setJob({ selection, phase: 'failed', message: friendly(settled.errors) });
        return;
      }
      const changed = (settled.sections || [])
        .filter((section) => section.text !== (settled.base_sections?.find((entry) => entry.key === section.key)?.text || ''))
        .map((section) => section.title);
      const result = read(await publishChipGuidanceDraft(settled.id, { draft_version: settled.draft_version, base_version: settled.base_version }));
      if (token !== jobSequence.current || !mounted.current) return;
      setJob(null);
      await load();
      if (mounted.current) current.current.onPublished?.(result, selection, changed);
    } catch (failure) {
      if (token === jobSequence.current && mounted.current) {
        // "Unknown error" is the request layer's word for "no response at all" (requestHandler.js):
        // the API was unreachable or restarting mid-call — live, a nodemon restart during an approval.
        setJob({ selection, phase: 'failed', message: /STALE/.test(failure.code || '')
          ? 'The guidance changed while this rule was being merged. Nothing was lost; try again to merge against the latest version.'
          : failure.message === 'Unknown error'
            ? 'The server did not respond — it may be restarting. Nothing was published; try again in a moment.'
            : failure.message });
      }
    }
  }, [load]);
  const apply = useCallback((payload) => run(() => createChipGuidanceDraft(payload), payload), [run]);
  useImperativeHandle(ref, () => ({ apply, refresh: load }), [apply, load]);

  const cancelJob = () => { jobSequence.current += 1; setJob(null); };
  const conflicts = job?.draft?.conflicts || [];
  const resolutionsReady = conflicts.length > 0 && conflicts.every((conflict) => {
    const value = resolutions[conflictId(conflict)];
    if (!value?.action) return false;
    return value.action !== 'custom' || (value.text || '').trim().length >= 10;
  });
  const resolveConflicts = () => {
    const draft = job.draft;
    run(() => updateChipGuidanceDraft(draft.id, { draft_version: draft.draft_version,
      resolutions: conflicts.map((conflict) => {
        const value = resolutions[conflictId(conflict)];
        return { conflict_id: conflictId(conflict), action: value.action, ...(value.action === 'custom' ? { text: value.text.trim() } : {}) };
      }) }), job.selection);
  };
  const doRestore = async () => {
    if (loading || !restore || Number(restore.customer_id) !== Number(customerId) || Number(guidance?.customer_id) !== Number(customerId)) return;
    setBusy(true); setError('');
    try {
      const result = read(await restoreChipGuidanceRevision(restore.id, restore.base_version));
      if (!mounted.current) return;
      setRestore(null); await load(); if (mounted.current) current.current.onPublished?.(result, null);
    } catch (failure) { if (mounted.current) setError(`${failure.message} Refresh to check the latest published version before restoring.`); }
    finally { if (mounted.current) setBusy(false); }
  };
  return <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
      <Typography variant="h6">Published Guidance</Typography>
      <Chip size="small" label={customerId === 0 ? 'Global · all bakeries' : `Customer scope #${customerId}`} />
      <Box sx={{ flexGrow: 1 }} />
      <Button startIcon={<Refresh />} disabled={loading} onClick={load} aria-label="Refresh published guidance">Refresh</Button>
      <Tooltip title="Rebuilds every section of this scope's guidance from all of its active rules and publishes the updated guidance. Use it after many approvals, or when a section's wording looks inconsistent."><span>
        <Button variant="outlined" disabled={loading || busy || !!job || !guidance?.llm_provider} onClick={() => apply({ customer_id: customerId, suggestion_ids: [], active_changes: [], initial_consolidation: true })}>Consolidate existing instructions</Button>
      </span></Tooltip>
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Approved rules are merged into the matching section automatically. Consolidation includes every active approved source instruction in this scope, including older instructions beyond the previous prompt limit.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {loading && <CircularProgress size={20} />}
    {guidance?.legacy_text && !guidance?.published_revision_id && <Alert severity="info" sx={{ mb: 2 }}>Current legacy guidance stays active until a consolidated version is published.<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{guidance.legacy_text}</Typography></Alert>}
    {!!guidance?.other_scopes?.length && <Alert severity="warning" sx={{ mb: 2 }}>
      Customer-specific guidance also exists for {guidance.other_scopes.map((entry) => `${entry.customer_name || `customer #${entry.customer_id}`} (Version ${entry.version})`).join(', ')} and overrides the Global guidance for those customers' users. This page manages the Global guidance only.
    </Alert>}
    <Tabs value={detailsTab} onChange={(_, value) => setDetailsTab(value)} aria-label="Published Guidance Details" sx={{ borderBottom: '1px solid #ddd', mb: 2 }}>
      <Tab value="guidance" label="Guidance" />
      <Tab value="history" label="History & Sources" />
    </Tabs>
    <Box role="tabpanel" id="guidance-details-panel-guidance" aria-labelledby="guidance-details-tab-guidance" hidden={detailsTab !== 'guidance'}>
    {(guidance?.sections || []).map((section) => <Box key={section.key} id={`guidance-section-${section.key}`} data-testid={`guidance-section-${section.key}`} data-highlighted={highlightKey === section.key ? 'true' : undefined}
      sx={{ mb: 2, p: 1, mx: -1, borderRadius: 1, transition: 'background-color 300ms', backgroundColor: highlightKey === section.key ? 'action.selected' : 'transparent' }}>
      <Typography variant="subtitle1">{section.title}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{section.text || 'No guidance in this section.'}</Typography>
      <SourceLinks refs={section.source_refs} onSource={onSource} />
    </Box>)}
    {guidance && <Box sx={{ mb: 2 }} data-testid="active-policies">
      <Typography variant="subtitle1">Active policies</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Settings CHIP's tools read directly from approved rules — they apply even where wording alone cannot (navigation, request-list routing, table size).</Typography>
      {Object.keys(guidance.policies || {}).length
        ? <Typography variant="body2">Policy settings are applied automatically.</Typography>
        : <Typography variant="body2">No policies are set; CHIP's tools use their original behavior.</Typography>}
     </Box>}
    </Box>
    <Box ref={historyPanel} role="tabpanel" id="guidance-details-panel-history" aria-labelledby="guidance-details-tab-history" hidden={detailsTab !== 'history'}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Source instructions & feedback history</Typography>
      {sourceHistory}
     </Box>
    <Dialog open={!!job} onClose={() => { if (job?.phase !== 'working') cancelJob(); }} fullWidth maxWidth="sm">
      {job?.phase === 'working' && <>
        <DialogTitle>Updating guidance</DialogTitle>
        <DialogContent><Stack direction="row" gap={1.5} alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={22} />
          <Typography>Merging the rule into the guidance…</Typography>
        </Stack></DialogContent>
      </>}
      {job?.phase === 'conflict' && <>
        <DialogTitle>Rules conflict — choose what to keep</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">The new rule contradicts existing guidance. Nothing is published until you choose.</Typography>
            {conflicts.map((conflict) => {
              const id = conflictId(conflict); const value = resolutions[id] || {};
              return <Paper variant="outlined" key={id} sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>{conflict.message || id}</Typography>
                <SourceLinks refs={[...(conflict.existing_refs || []), ...(conflict.suggestion_refs || [])]} onSource={onSource} />
                <FormControl size="small" fullWidth sx={{ mt: 1 }}><InputLabel id={`conflict-label-${id}`}>Resolve conflict</InputLabel>
                  <Select labelId={`conflict-label-${id}`} label="Resolve conflict" value={value.action || ''} onChange={(event) => setResolutions((values) => ({ ...values, [id]: { ...values[id], action: event.target.value } }))}>
                    <MenuItem value="keep_existing">Keep existing rule</MenuItem>
                    <MenuItem value="replace_with_suggestion">Use the new rule</MenuItem>
                    <MenuItem value="custom">Type corrected wording</MenuItem>
                  </Select></FormControl>
                {value.action === 'custom' && <TextField fullWidth label="Corrected wording" multiline minRows={2} sx={{ mt: 1 }} value={value.text || ''} onChange={(event) => setResolutions((values) => ({ ...values, [id]: { ...values[id], text: event.target.value } }))} inputProps={{ maxLength: 800 }} />}
              </Paper>;
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelJob}>Cancel</Button>
          <Button variant="contained" disabled={!resolutionsReady} onClick={resolveConflicts}>Apply and publish</Button>
        </DialogActions>
      </>}
      {job?.phase === 'failed' && <>
        <DialogTitle>Guidance not updated</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            <Alert severity="error">{job.message}</Alert>
            <Typography variant="body2" color="text.secondary">Nothing was published; the current guidance is unchanged.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelJob}>Close</Button>
          {job.selection && <Button variant="contained" onClick={() => run(() => createChipGuidanceDraft(job.selection), job.selection)}>Try again</Button>}
        </DialogActions>
      </>}
    </Dialog>
    <Dialog open={!!restore} onClose={() => { if (!busy) setRestore(null); }}><DialogTitle>Restore published guidance?</DialogTitle><DialogContent>Restore version {restore?.published_version || restore?.version || restore?.id} and its source instruction membership for {Number(restore?.customer_id) === 0 ? 'Global · all bakeries' : `customer scope #${restore?.customer_id}`}? This publishes a new version for the next CHIP message.</DialogContent><DialogActions><Button disabled={busy} onClick={() => setRestore(null)}>Cancel</Button><Button variant="contained" disabled={busy || loading || Number(restore?.customer_id) !== Number(customerId)} onClick={doRestore}>Confirm restore</Button></DialogActions></Dialog>
  </Paper>;
});

export default GuidancePanel;
